




import Vue from 'vue'
import Postmate from 'postmate'
import { trackGaPageView } from '@/util/helper'
import { mapState } from 'vuex'
import moment from 'moment-timezone'
import {
  User,
  UserState,
  Location,
  PrivateUser,
  CloneAccount,
} from '@/models'
import Company from '@/models/api/Company'
import defaults from '@/config'
import firebase from 'firebase/compat/app'
import depreciatedFeatures from '@/util/depreciated_features';
import LocationsService from '@/services/LocationsService'
import { OnboardingService } from '@/services'

interface OnboardingData  {
  industryServed: string,
  customerCount: string,
  haveWebsite: boolean,
  websiteUrl: string,
  tools: string[],
  password: string,
  address: string,
  addressLine: string,
  country: string,
  region: string,
  city: string,
  zipcode: string,
  tandc: boolean,
  snapshotId: string,
  isReselling: boolean,
  privacyPolicy: boolean
}

export default Vue.extend({
  data() {
    return {
      handshake: {} as any,
      onboardingData: {} as OnboardingData,
      baseUrl: defaults.baseUrl,
      newOnboardingFrontend: defaults.newOnboardingFrontend,
      firstName: '',
      childLoaded: false,
      authKey: '',
      importing: false,
      appLoader: false,
      company: {} as Company
    }
  },
  async mounted() {
    const user = new User(await this.$store.dispatch('user/get'))
    if (user) {
      this.company = await Company.getById(user.companyId)
      this.loadMicroApp()
      this.childLoaded = true
    }
    window.addEventListener('popstate', () => {
      this.handshake.then(child => {
        child.call('routeChange', { path: this.$route.path, direction: 'back' })
      })
    })
  },
  computed: {
    ...mapState('user', {
      user: (s: UserState) => {
        return s.user ? new User(s.user) : undefined
      },
    }),
  },
  methods: {
    async loadMicroApp() {
      const isVerificationPending = await this.isVerificationPending()
      const isOnbPending = this.isOnboardingPending
      if (isVerificationPending || isOnbPending) {
        this.handshakeIframe()
      } else {
        this.isLocationPending()
      }
    },
    async isOnboardingPending() {
      try {
        if (this.company && !this.company.onboardingInfo.pending) {
          return false
        }
        return true
      } catch (error) {
        return true
      }
    },
    async isLocationPending() {
      try {
        if (this.company?.onboardingInfo.location || this.company?.onboardingInfo.conversationDemo) {
          const isExists = await this.isLocationExists()
          if (!isExists) {
            if (this.company.onboardingInfo.customerCount !== 'I don’t have any customers yet' &&
            this.company?.onboardingInfo.location) {
              this.$router.push({ name: 'launchpad' })
              return false
            } else {
              this.createDefaultLocation('')
              return false
            }
          }
        }
        this.$router.push({ name: 'agency_dashboard' })
        return false
      } catch (error) {
        this.$router.push({ name: 'agency_dashboard' })
        return false
      }
    },
    async isVerificationPending() {
      try {
        let isVerified = false
        const verifyStatus = await this.$store.dispatch('onboarding/verifyStatus')
        const isVerificationRoute = this.$router.currentRoute?.meta?.verificationRoute

        if (verifyStatus.emailVerified && verifyStatus.phoneNumberVerified) {
          isVerified = true
        }

        if (isVerified) {
          return false
        }

        return true
      } catch (error) {
        console.log(error)
        return true
      }
    },
    async handshakeIframe() {
      try {
        let snapshotName, snapshotType
        this.firstName = this.user?.firstName
        const locationId = this.company?.onboardingInfo?.locationId
        this.authKey = await firebase.auth().currentUser.getIdToken()

        if (process.env.NODE_ENV === 'development') Postmate.debug = true
        this.handshake = new Postmate({
          container: this.$refs.onboardingV2 as HTMLElement,
          url: `${this.newOnboardingFrontend}${this.$route.path}`,
          name: 'onboarding-v2-home',
          classListArray: ['onboarding-iframe'],
          model: {
            websiteSignup: this.user?.token ? true : false,
            firstName: this.user?.firstName,
            authKey: this.authKey,
            isPasswordPending: this.user?.isPasswordPending,
            locationId: locationId,
            snapshotId: this.company.onboardingInfo.snapshotId,
            snapshotName: snapshotName,
            snapshotType: snapshotType,
            isAgencyUser: this.user?.type == User.TYPE_AGENCY,
            userId: this.user?.id,
            company: {
              email: this.company?.email,
              phone: this.company?.phone || this.user?.phone,
              id: this.company?.id,
              relationshipNumber: this.company?.relationshipNumber
            },
            
          },
        })
        this.handshake.then(child => {
          child.on('spm-ts', (data: any) => {
            if (data.newTab) {
              let routeUrl = this.$router.resolve(data.router)
              window.open(routeUrl.href, '_blank')
              console.log(routeUrl.href)
            } else {
              this.$router.push(data.router)
            }
          })
          child.on('ga-event', (data: any) => {
            trackGaPageView(data)
          })
          child.on('load', data => {
            document.getElementById('app').classList.remove('loading')
          })
          child.on('onboarding-data', data => {
            this.onboardingData = JSON.parse(data)
            this.$nextTick(() => {
              this.updateData()
            })
          })
          child.on('onboarding-dashboard', () => {
            this.goToDashboard()
          })
          child.on('onboarding-continue', async() => {
            this.gotToSearch()
          })
          child.on('onboarding-launchpad', data => {
            if (data) {
              const payload = JSON.parse(data)
              this.onboardingData.locationId = payload.locationId
              this.updateLocation(payload)
            }
            this.goToLaunchpad()
          })
          child.on('onboarding-location', data => {
            const payload = JSON.parse(data)
            this.onboardingData.locationId = payload.locationId
            this.onboardingLocation()
          })
          child.on('onboarding-complete', () => {
            this.onboardingComplete()
          })
          child.on('default-location', () => {
            this.createDefaultLocation()
          })
          child.on('default-location-launchpad', () => {
            this.createDefaultLocation('launchpad')
          })
          child.on('refresh-token', async () => {
            const tokenId = await this.getTokenId()
            child.call('updatedToken', tokenId)
          })
        })
      } catch (err) {}
    },
    async createDefaultLocation(redirect: string) {
      this.showHideLoader()
      // Creating new blank location
      const body = {
        businessName: this.company.name || 'Default account',
        phone: this.user.phone,
        email: this.user.email,
        isContactCreate: true,
        firstName: this.user.firstName || '',
        lastName: this.user.lastName || '',
        address: this.company.address,
        city: this.company.city,
        state: this.company.state,
        postalCode: this.company.postalCode,
        website: this.company.website || ''
      }

      const options = {
        headers: {
          'Content-Type': 'application/json',
          'internal-channel': 'web',
        },
      }

      try {
        const location = await this.publicApi.post(
          '/v1/locations/onboarding',
          body,
          options
        )
        if (location.status == 200) {
          this.showHideLoader()
          await Location.collectionRef().doc(location.data.id).update({
            depreciated_features: depreciatedFeatures
          });
          const company = await Company.getById(this.company.id)
          company.onboardingInfo = {
            ...company.onboardingInfo,
            locationId: location.data.id,
            location: false,
            conversationDemo: false
          }
          await company.save()
          // Initializing default location with default snapshot
          try {
            const snapshotType = 'default'
            await this.$snapshotBackend.post(
              `/snapshot/${snapshotType}/load_bg/${location.data.id}`,
              {
                snapshotType,
              }
            )
            this.$router.push({ name: 'agency_dashboard' })
            // this.handshake.then(child => {
            //   child.call('conversationPage', {
            //     locationId: location.data.id,
            //   })
            // })
            // this.$router.push({ name: 'onboarding_micro_app_conversation' })
          } catch (err) {
            this.showHideLoader()
            this.$router.push({ name: 'agency_dashboard' })
          }
        }
      } catch (error) {
        await this.skipOnboarding()
        this.showHideLoader()
        this.$router.push({ name: 'agency_dashboard' })
      }
    },
    async skipOnboarding() {
      try {
        const company = await Company.getById(this.company.id);
        company.onboardingInfo = {
          ...company.onboardingInfo,
          location: false,
          conversationDemo: false
        }
        await company.save()
      } catch (err) {
        console.log('Onboarding Skip Error', err)
      }
    },
    async isLocationExists() {
      let isLocation = false
      this.showHideLoader()
      try {
        if (this.company) {
          let locations = []
          await LocationsService.search(this.company.id, false, undefined, undefined, 50).then((response) => {
              locations = response.locations.map((location: any) => {
                  return {...location, id: location._id}
              })
          }).catch(err => {
          })
          if (locations.length > 0) {
            isLocation = true
            await this.skipOnboarding()
          }
        }
      } catch (err) { }
      this.showHideLoader()
      return isLocation
    },
    async createDefaultCampaign(locationId: string) {
      try {
        // Adding user to a default onboarding campaign.
        let response = await this.$http.post('/onboarding/campaign', {
          location_id: locationId,
          first_name: this.user.firstName,
          last_name: this.user.lastName,
          name: this.user.fullName || 'New User',
          email: this.user.email,
          phone: this.user.phone,
        })
      } catch (err) {
        console.error(err)
      }
    },
    async updateLocation(payload: any) {
      let company = await Company.getById(this.company.id);
      company.onboardingInfo = {
        ...company.onboardingInfo,
        locationId: payload.locationId,
        location: false
      }
      await company.save()
    },
    async updateData() {
      const company = await Company.getById(this.company.id);
      company.onboardingInfo = {
        industryServed: this.onboardingData.industryServed,
        customerCount: this.onboardingData.customerCount,
        haveWebsite: this.onboardingData.haveWebsite,
        websiteUrl: this.onboardingData.websiteUrl,
        tools: this.onboardingData.tools,
        pending: false,
        location: true,
        snapshotId: company?.onboardingInfo.snapshotId
      }
      if (this.onboardingData.address) {
        if (this.onboardingData.addressLine) {
          this.onboardingData.address =
            this.onboardingData.address + ', ' + this.onboardingData.addressLine
        }
        company.address = this.onboardingData.address
        company.city = this.onboardingData.city
        company.country = this.onboardingData.country
        company.postalCode = this.onboardingData.zipcode
        company.state = this.onboardingData.region
        company.termsOfServiceVersion = defaults.tosVersion
        company.termsOfServiceAcceptedBy = this.user.id
        company.termsOfServiceAcceptedDate = moment().utc()
        company.privacyPolicyVersion = defaults.privacyPolicyVersion
        company.privacyPolicyAcceptedBy = this.user.id
        company.privacyPolicyAcceptedDate = moment().utc()
        company.affiliatePolicyVersion = defaults.affiliatePolicyVersion
        company.affiliatePolicyAcceptedBy = this.user.id
        company.affiliatePolicyAcceptedDate = moment().utc()
        company.isReselling = this.onboardingData.isReselling
      }
      await company.save()

      // call onboardingTagTaskScheduler API to add or remove tags
      try {
        await OnboardingService.onboardingTagTaskScheduler({companyId: this.company.id})
      } catch (error) {
        console.log('Erorr in onboardingTagTaskScheduler', error)
      }

      try {
        if (this.onboardingData.password) {
          this.$store.commit('user/updatePasswordUpdatedInOnboarding', true)
          let user = await User.getById(this.user.id)
          let currentPrivateUser = new PrivateUser(
            await user.privateDataRef.get()
          )
          currentPrivateUser.passwordHash = this.onboardingData.password
          await currentPrivateUser.save()
          await user.passwordChanged()
        }

        const onboardingInfo = this.updateOnboardingInfo()
        const stripeUpdate = this.updateStripeCustomer()
        await this.createTosLog()

        await Promise.all([onboardingInfo, stripeUpdate])

        let _self = this
        //adding delay so vuex store get time to updated
        setTimeout(function () {
          _self.verfiyEmailAndPhone()
        }, 2000)
      } catch (error) {
        console.log('Erorr updating onboarding data', error)
      }
    },
    async verfiyEmailAndPhone() {
      this.handshake.then(child => {
        this.$router.push({
          name: 'onboarding_micro_app_email_verify',
          replace: true,
        })
        child.call('routeChange', { name: 'businessEmailVerifyPage' })
      })
    },
    async gotToSearch() {
      if(this.company?.onboardingInfo.snapshotId) {
        this.$router.push({ 
          name: 'import_account_template', 
          params: { share_id: this.company?.onboardingInfo.snapshotId} 
        })
      } else if (
        this.onboardingData.customerCount !== 'I don’t have any customers yet'
      ) {
        this.$router.push({
          name: 'launchpad',
          replace: true,
        })
      } else {
        await this.createDefaultLocation('')
      }
    },
    async goToLaunchpad() {
      let _self = this
      await this.onboardingComplete()
      let params = {}
      if (this.company.onboardingInfo.locationId) {
        params = {
          location_id: this.company.onboardingInfo.locationId,
        }
      }
      _self.$router.push({
        name: 'location_launchpad',
        params,
      })
    },
    async goToDashboard() {
      let _self = this
      await this.onboardingComplete()
      let params = {}
      if (this.company.onboardingInfo.locationId) {
        params = {
          location_id: this.company.onboardingInfo.locationId,
        }
      }
      setTimeout(function () {
        _self.$router.push({
          name: 'dashboard',
          replace: true,
          params,
        })
      }, 2000)
    },
    async onboardingComplete() {
      const company = await Company.getById(this.company.id);
      company.onboardingInfo = {
        ...company.onboardingInfo,
        conversationDemo: false
      }
      await company.save()
      let user = await User.getById(this.user.id)
      user.token = ''
      await user.save()
    },
    async onboardingLocation() {
      const company = await Company.getById(this.company.id);
      company.onboardingInfo = {
        ...company.onboardingInfo,
        location: false,
        locationId: this.onboardingData.locationId,
        conversationDemo: true
      }
      await company.save()
      await this.updateOnboardingInfo()
      const cloneAccount = await CloneAccount.getByCompanyId(this.company.id)
      if (cloneAccount) {
        await this.$snapshotBackend.post(
          `/snapshot/${cloneAccount[0].id}/load_bg/${this.onboardingData.locationId}`,
          {
            snapshotType: cloneAccount[0].type,
          }
        )
      }
    },
    async updateOnboardingInfo() {
      try {
        // might fail due to 401 [an-authorized]
        const customFields = await this.$http.post('/onboarding', {
          client_count: this.onboardingData.customerCount,
          niche: this.onboardingData.industryServed,
          previous_product: this.onboardingData.tools,
          website: this.onboardingData.websiteUrl,
          is_reselling: this.onboardingData.isReselling ? 'Yes' : 'No',
        })
      } catch (err) {
        console.error('Failed to update onboarding-info in smart-list !!', err)
      }
    },
    async preventPasswrodChangeLogout() {
      await this.user.passwordChanged()
    },
    async createTosLog() {
      try {
        await OnboardingService.createTosLogs({
          userId: this.user.id,
          companyId: this.company.id,
          version: defaults.tosVersion
        })
      } catch (error) {
        console.log(error)
      }
    },
    async updateStripeCustomer() {
      try {
        if (this.onboardingData.address && this.company.stripeId) {
          let tosAcceptedDate = moment()
          await this.$http.put('/onboarding/customer_update', {
            metadata: {
              reseller: this.onboardingData.isReselling ? 'Yes' : 'No',
              tos_accepted_by: this.user?.fullName,
              pp_accepted_by: this.user?.fullName,
              tos_accepted_date: tosAcceptedDate.utc()
                .format('MMM. D, YYYY [at] h:mm A z'),
              pp_accepted_date: tosAcceptedDate.utc()
                .format('MMM. D, YYYY [at] h:mm A z'),
              tos_version: moment(defaults.tosVersion).format('MMM DD YYYY'),
              pp_version: moment(defaults.privacyPolicyVersion).format('MMM DD YYYY'),
              niche: this.onboardingData.industryServed,
              client_count: this.onboardingData.customerCount,
              previous_product: this.onboardingData.tools.join(','),
            },
          })
        }
      } catch (error) {
        console.log(error)
      }
    },
    showHideLoader() {
      const element = document.getElementById('app')
      const htmlTag = document.getElementsByTagName('html')[0]
      if (!this.appLoader) {
        this.$store.commit('setAgencyLoaderState',true)

        htmlTag.className = 'overflow-hidden lead-connector'
      } else if (element) {
        this.$store.commit('setAgencyLoaderState',false)
        htmlTag.className = ''
      }
      this.appLoader = !this.appLoader
    },
    destroy() {
      //@ts-ignore
      this.handshake.then((child: any) => {
        child.destroy()
      })
    },
    async getTokenId() {
      try {
        const tokenId = await firebase.auth().currentUser?.getIdToken()
        return tokenId
      } catch (error) {
        return ''
      }
    },
  },
  beforeDestroy() {
    this.destroy()
    window.removeEventListener('popstate', () => console.log('remove popstate'))
  },
})
